/**
 * useMediaCookie — keeps the `sr_media` HLS cookie alive while a room is open.
 *
 * - refreshes on a fixed cadence (MEDIA_COOKIE_REFRESH_MS, well inside the
 *   30-minute cookie TTL),
 * - refreshes when the viewer returns (tab visible / network back),
 * - recovers from a mid-playback media error (segment 401/403 because the
 *   cookie lapsed anyway, e.g. a laptop slept): refresh, reload the source and
 *   let the caller re-sync to the room.
 */
import { useEffect, useRef } from 'react';
import { MEDIA_COOKIE_REFRESH_MS } from '@syncreel/shared';
import { refreshMediaToken } from '../api/client';
import { createMediaTokenRefresher } from '../lib/mediaToken';
import { onViewerReturn } from '../lib/returnTriggers';
import type { Player } from '../player/useVideoJs';
import { logger } from '../api/logger';

// MediaError codes: 2 = NETWORK, 4 = SRC_NOT_SUPPORTED (what a 401 on the
// manifest/segments surfaces as in some browsers).
const RECOVERABLE_ERROR_CODES = new Set([2, 4]);
// WHY: never loop reloading a source that is genuinely broken.
const MIN_RECOVERY_GAP_MS = 15_000;

/** Args for {@link useMediaCookie}. */
export function useMediaCookie(args: {
  roomId: string;
  player: Player | null;
  /** Called after the source was reloaded so the caller can re-sync. */
  onRecovered: () => void;
}): void {
  const { roomId, player } = args;
  const onRecoveredRef = useRef(args.onRecovered);
  onRecoveredRef.current = args.onRecovered;

  useEffect(() => {
    const refresher = createMediaTokenRefresher({
      refresh: () => refreshMediaToken(roomId),
      intervalMs: MEDIA_COOKIE_REFRESH_MS,
      onError: (err) => logger.warn('media cookie refresh failed', err),
    });
    refresher.start();
    const offReturn = onViewerReturn(() => {
      void refresher.refreshNow({ force: true });
    });

    let lastRecoveryAt = 0;
    const onError = (): void => {
      const err = player?.error();
      if (!player || !err || !RECOVERABLE_ERROR_CODES.has(err.code)) return;
      if (Date.now() - lastRecoveryAt < MIN_RECOVERY_GAP_MS) return;
      lastRecoveryAt = Date.now();
      void refresher.refreshNow({ force: true }).then((ok) => {
        if (!ok || player.isDisposed()) return;
        const src = player.currentSource();
        // WHY: video.js types omit the documented `error(null)` clear.
        (player as unknown as { error(e: null): void }).error(null);
        player.src(src);
        onRecoveredRef.current();
      });
    };
    player?.on('error', onError);

    return () => {
      refresher.stop();
      offReturn();
      player?.off('error', onError);
    };
  }, [roomId, player]);
}
